import { ReactNode } from 'react'
import { Icon, IconName } from './Icon'

type Tone = 'error' | 'ok' | 'info'

export function Alert({ tone = 'error', title, children, actions }: { tone?: Tone; title: ReactNode; children?: ReactNode; actions?: ReactNode }) {
  const cls = tone === 'error' ? 'alert' : `alert ${tone}`
  const icon: IconName = tone === 'error' ? 'alert' : tone === 'ok' ? 'okCircle' : 'info'
  return (
    <div className={cls} role={tone === 'error' ? 'alert' : 'status'}>
      <span className="alert-icon">
        <Icon name={icon} size={22} />
      </span>
      <div className="alert-body">
        <div className="ttl">{title}</div>
        {children && <div className="txt">{children}</div>}
        {actions && <div className="btns">{actions}</div>}
      </div>
    </div>
  )
}

export function EmptyState({
  icon = 'book',
  title,
  children,
  actions,
  error,
  headingLevel = 1,
}: {
  icon?: IconName
  title: ReactNode
  children?: ReactNode
  actions?: ReactNode
  error?: boolean
  headingLevel?: 1 | 2
}) {
  const Heading = headingLevel === 1 ? 'h1' : 'h2'
  return (
    <div className="state" role={error ? 'alert' : undefined}>
      <div className={error ? 'state-icon err' : 'state-icon'}>
        <Icon name={error ? 'alert' : icon} size={32} strokeWidth={2} />
      </div>
      <Heading>{title}</Heading>
      {children && <p>{children}</p>}
      {actions}
    </div>
  )
}

export function Loading({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div aria-busy="true">
      <div className="loading-label" role="status">
        <span className="spin" aria-hidden="true" />
        {label}
      </div>
      <div aria-hidden="true">{children}</div>
    </div>
  )
}

export function Sk({ w = '100%', h, ratio, style }: { w?: string; h?: number; ratio?: string; style?: React.CSSProperties }) {
  return <div className="sk" style={{ width: w, height: h, aspectRatio: ratio, ...style }} />
}

/** Placeholder for one post in a feed */
export function PostSkeleton() {
  return (
    <div className="vstack gap-md mb-4xl">
      <Sk w="70%" h={40} />
      <Sk w="30%" h={16} />
      <div className="hstack gap-sm">
        <Sk w="96px" h={32} />
        <Sk w="64px" h={32} />
      </div>
      <Sk ratio="1 / 1" />
      <div className="thumbs">
        {Array.from({ length: 6 }, (_, i) => (
          <Sk key={i} ratio="1 / 1" />
        ))}
      </div>
      <Sk h={18} />
      <Sk w="80%" h={18} />
    </div>
  )
}
